import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCertificates, createCertificate, updateCertificate,
  getCourses, getUsers,
  downloadCertificate,
  getExternalCertificates, deleteExternalCertificate, downloadExternalCertificate,
  importLegacyCertificates, getLegacyImportStatus, getLegacyStats, searchLegacyCertificates, deleteAllLegacy,
  assignExternalCertToUser, unassignExternalCert,
  assignLegacyCertToUser, unassignLegacyCert,
} from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Award, Download, Plus, Edit, Settings, Upload, Trash2, ExternalLink, RefreshCw, Search, Archive, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import AssignUserDialog from '../../components/AssignUserDialog';

export const ManageCertificates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [certificates, setCertificates] = useState([]);
  const [externalCerts, setExternalCerts] = useState([]);
  const [assignDialog, setAssignDialog] = useState({ open: false, type: null, certId: null });
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auto-select tab from ?tab= URL param (e.g. after upload redirect)
  const initialTab = new URLSearchParams(location.search).get('tab') || 'internal';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Legacy
  const [legacyStats, setLegacyStats] = useState(null);
  const [legacyImportStatus, setLegacyImportStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [legacyQuery, setLegacyQuery] = useState('');
  const [legacyResults, setLegacyResults] = useState([]);
  const [legacyTotal, setLegacyTotal] = useState(0);
  const [legacyPage, setLegacyPage] = useState(1);
  const [legacySearching, setLegacySearching] = useState(false);
  const LEGACY_LIMIT = 15;

  // Issue dialog
  const [issueOpen, setIssueOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');

  // Edit internal dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [editStatus, setEditStatus] = useState('valid');
  const [editReason, setEditReason] = useState('');

  // QR download options dialog
  const [qrOpen, setQrOpen] = useState(false);
  const [qrCertId, setQrCertId] = useState(null);
  const [qrPosition, setQrPosition] = useState('bottom-right');
  const [qrX, setQrX] = useState('');
  const [qrY, setQrY] = useState('');
  const [qrZ, setQrZ] = useState('');

  // Cross-tab search (internal + external)
  const [search, setSearch] = useState('');
  const matches = (text) =>
    !search || (text || '').toString().toLowerCase().includes(search.toLowerCase());
  const filterInternal = (cert) =>
    matches(cert.code) ||
    matches(cert.user_name) ||
    matches(cert.user_email) ||
    matches(cert.course_title);
  const filterExternal = (cert) =>
    matches(cert.code) ||
    matches(cert.owner_name) ||
    matches(cert.owner_email) ||
    matches(cert.course_name);

  useEffect(() => {
    if (user && !['superadmin', 'admin', 'professor'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
    // Pre-fetch legacy stats to show count in tab without clicking it
    fetchLegacyStats();
  }, [user, navigate]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const results = await Promise.allSettled([
        getCertificates(),       // [0]
        getCourses(),            // [1]
        getUsers(),              // [2]
        getExternalCertificates(), // [3]
      ]);

      const [certsResult, coursesResult, usersResult, extResult] = results;

      if (certsResult.status === 'fulfilled') {
        setCertificates(certsResult.value.data);
      } else {
        console.error('Error cargando certificados de cursos:', certsResult.reason);
        setCertificates([]);
      }

      if (coursesResult.status === 'fulfilled') {
        setCourses(coursesResult.value.data);
      } else {
        console.error('Error cargando cursos:', coursesResult.reason);
        setCourses([]);
      }

      if (usersResult.status === 'fulfilled') {
        setStudents(usersResult.value.data.filter(u => u.role === 'student'));
      } else {
        console.error('Error cargando usuarios:', usersResult.reason);
        setStudents([]);
      }

      if (extResult.status === 'fulfilled') {
        setExternalCerts(extResult.value.data);
      } else {
        console.error('Error cargando certificados externos:', extResult.reason);
        setExternalCerts([]);
      }

      // Show a partial warning only if everything failed
      const allFailed = results.every(r => r.status === 'rejected');
      if (allFailed) {
        toast.error('Error al cargar datos. Recarga la página.');
      }
    } catch (err) {
      console.error('fetchData unexpected error:', err);
      toast.error('Error inesperado al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchLegacyStats = useCallback(async () => {
    try {
      const [statsRes, statusRes] = await Promise.all([
        getLegacyStats(),
        getLegacyImportStatus(),
      ]);
      setLegacyStats(statsRes.data);
      setLegacyImportStatus(statusRes.data);
    } catch { /* ignore */ }
  }, []);

  const handleLegacySearch = useCallback(async (q, p) => {
    setLegacySearching(true);
    try {
      const res = await searchLegacyCertificates(q, p, LEGACY_LIMIT);
      setLegacyResults(res.data.results);
      setLegacyTotal(res.data.total);
      setLegacyPage(p);
    } catch {
      toast.error('Error al buscar');
    } finally {
      setLegacySearching(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'legacy') {
      fetchLegacyStats();
      if (legacyResults.length === 0) handleLegacySearch('', 1);
    }
  }, [activeTab]); // eslint-disable-line

  const handleImport = async () => {
    setImporting(true);
    try {
      await importLegacyCertificates();
      toast.success('Importación iniciada. Revisa el estado en unos segundos.');
      // Poll for status
      const poll = setInterval(async () => {
        try {
          const res = await getLegacyImportStatus();
          setLegacyImportStatus(res.data);
          if (res.data.status === 'done') {
            clearInterval(poll);
            setImporting(false);
            fetchLegacyStats();
            handleLegacySearch(legacyQuery, 1);
            toast.success(`Importación completa: ${res.data.inserted} nuevos, ${res.data.updated} actualizados`);
          }
        } catch { clearInterval(poll); setImporting(false); }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar importación');
      setImporting(false);
    }
  };

  const handleDeleteAllLegacy = async () => {
    if (!window.confirm('¿Eliminar todos los certificados legacy importados? Esta acción no se puede deshacer.')) return;
    try {
      const res = await deleteAllLegacy();
      toast.success(`${res.data.deleted} certificados eliminados`);
      setLegacyStats(null);
      setLegacyImportStatus(null);
      setLegacyResults([]);
      setLegacyTotal(0);
    } catch {
      toast.error('Error al eliminar');
    }
  };

  // Issue certificate
  const handleIssue = async () => {
    if (!selectedStudent || !selectedCourse) {
      toast.error('Selecciona un estudiante y un curso');
      return;
    }
    try {
      await createCertificate({ user_id: selectedStudent, course_id: selectedCourse });
      toast.success('¡Certificado emitido!');
      setIssueOpen(false);
      setSelectedStudent('');
      setSelectedCourse('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al emitir certificado');
    }
  };

  // Open edit dialog
  const openEdit = (cert) => {
    setEditingCert(cert);
    setEditStatus(cert.status);
    setEditReason(cert.revoked_reason || '');
    setEditOpen(true);
  };

  // Save edit
  const handleEdit = async () => {
    try {
      await updateCertificate(editingCert.id, {
        status: editStatus,
        revoked_reason: editStatus === 'revoked' ? editReason : null
      });
      toast.success('Certificado actualizado');
      setEditOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  // QR download options
  const openQrOptions = (certId) => {
    setQrCertId(certId);
    setQrPosition('bottom-right');
    setQrX('');
    setQrY('');
    setQrZ('');
    setQrOpen(true);
  };

  const handleQrDownload = () => {
    const params = {
      qr_position: qrPosition,
      qr_x: qrPosition === 'custom' ? qrX || undefined : undefined,
      qr_y: qrPosition === 'custom' ? qrY || undefined : undefined,
      qr_z: qrZ || undefined,
    };
    const url = downloadCertificate(qrCertId, params);
    window.open(url, '_blank');
    setQrOpen(false);
  };

  // Delete external cert
  const handleDeleteExternal = async (id, name) => {
    if (!window.confirm(`¿Eliminar el certificado externo de "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteExternalCertificate(id);
      toast.success('Certificado externo eliminado');
      fetchData();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleAssignUser = async (user) => {
    const { type, certId } = assignDialog;
    try {
      if (type === 'external') {
        await assignExternalCertToUser(certId, user.id);
      } else if (type === 'legacy') {
        await assignLegacyCertToUser(certId, user.id);
      }
      toast.success(`Certificado asignado a ${user.name || user.email}`);
      fetchData();
      // refresh legacy search results too
      if (type === 'legacy') handleLegacySearch(legacyQuery, legacyPage);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al asignar');
    }
  };

  const handleUnassign = async (type, certId) => {
    if (!window.confirm('¿Quitar la asignación de usuario a este certificado?')) return;
    try {
      if (type === 'external') await unassignExternalCert(certId);
      else await unassignLegacyCert(certId);
      toast.success('Asignación eliminada');
      fetchData();
      if (type === 'legacy') handleLegacySearch(legacyQuery, legacyPage);
    } catch {
      toast.error('Error al desasignar');
    }
  };

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="p-6 md:p-12 bg-secondary min-h-screen" data-testid="manage-certificates-page">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary mb-2">Gestionar Certificados</h1>
            <p className="text-muted-foreground">Emite y administra certificados de finalización</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/certificates/upload-external')}
              data-testid="upload-external-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Subir Externo
            </Button>

            <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
              <DialogTrigger asChild>
                <Button data-testid="issue-certificate-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Emitir Certificado
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Emitir Nuevo Certificado</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Estudiante *</label>
                    <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                      <SelectTrigger data-testid="select-student">
                        <SelectValue placeholder="Selecciona un estudiante" />
                      </SelectTrigger>
                      <SelectContent>
                        {students.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Curso *</label>
                    <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                      <SelectTrigger data-testid="select-course">
                        <SelectValue placeholder="Selecciona un curso" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleIssue} data-testid="confirm-issue-btn">Emitir Certificado</Button>
                    <Button variant="outline" onClick={() => setIssueOpen(false)}>Cancelar</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-3 bg-muted p-1 rounded-lg w-fit">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'internal' ? 'bg-background shadow text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('internal')}
            data-testid="tab-internal"
          >
            Certificados de Cursos ({certificates.length})
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'external' ? 'bg-background shadow text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('external')}
            data-testid="tab-external"
          >
            Certificados Externos ({externalCerts.length})
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'legacy' ? 'bg-background shadow text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('legacy')}
            data-testid="tab-legacy"
          >
            Certificados Legacy {legacyStats ? `(${legacyStats.total})` : ''}
          </button>
        </div>

        {/* Cross-tab search (visible only for internal + external tabs; legacy has its own) */}
        {(activeTab === 'internal' || activeTab === 'external') && (
          <div className="mb-6 flex items-center gap-2 max-w-xl" data-testid="cert-search-block">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por código, nombre, email o curso..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="cert-search-input"
              />
            </div>
            {search && (
              <Button variant="outline" size="sm" onClick={() => setSearch('')} data-testid="cert-search-clear">
                Limpiar
              </Button>
            )}
          </div>
        )}

        {/* ── Internal certificates ── */}
        {activeTab === 'internal' && (
          <>
            {/* Edit Internal Dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Editar Certificado</DialogTitle>
                </DialogHeader>
                {editingCert && (
                  <div className="space-y-4 py-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Estudiante: <strong>{editingCert.user_name}</strong></p>
                      <p className="text-sm text-muted-foreground">Curso: <strong>{editingCert.course_title}</strong></p>
                      <p className="text-xs font-mono text-muted-foreground mt-1">{editingCert.code}</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Select value={editStatus} onValueChange={setEditStatus}>
                        <SelectTrigger data-testid="edit-cert-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="valid">Válido</SelectItem>
                          <SelectItem value="revoked">Revocado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {editStatus === 'revoked' && (
                      <div className="space-y-2">
                        <Label>Razón de revocación</Label>
                        <Input value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="Motivo..." data-testid="edit-cert-reason" />
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleEdit} data-testid="save-cert-btn">Guardar</Button>
                      <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* QR Download Options Dialog */}
            <Dialog open={qrOpen} onOpenChange={setQrOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Opciones de Descarga con QR</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Posición del QR</Label>
                    <div className="flex flex-col gap-2">
                      {[
                        { value: 'bottom-right', label: 'Esquina inferior derecha' },
                        { value: 'bottom-left', label: 'Esquina inferior izquierda' },
                        { value: 'custom', label: 'Posición personalizada (X, Y)' },
                      ].map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="qr_position" value={opt.value} checked={qrPosition === opt.value} onChange={(e) => setQrPosition(e.target.value)} data-testid={`qr-pos-${opt.value}`} />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {qrPosition === 'custom' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label>X (px)</Label>
                        <Input type="number" value={qrX} onChange={(e) => setQrX(e.target.value)} placeholder="ej. 100" data-testid="qr-x-input" />
                      </div>
                      <div className="space-y-1">
                        <Label>Y (px)</Label>
                        <Input type="number" value={qrY} onChange={(e) => setQrY(e.target.value)} placeholder="ej. 500" data-testid="qr-y-input" />
                      </div>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label>Tamaño QR (Z) — px</Label>
                    <Input type="number" value={qrZ} onChange={(e) => setQrZ(e.target.value)} placeholder="Por defecto: 150" data-testid="qr-z-input" />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleQrDownload} data-testid="confirm-qr-download-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Descargar PDF
                    </Button>
                    <Button variant="outline" onClick={() => setQrOpen(false)}>Cancelar</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <div className="grid grid-cols-1 gap-4">
              {(() => {
                const filtered = certificates.filter(filterInternal);
                if (certificates.length === 0) {
                  return <Card><CardContent className="py-12 text-center"><p className="text-muted-foreground">No hay certificados de cursos aún</p></CardContent></Card>;
                }
                if (filtered.length === 0) {
                  return <Card><CardContent className="py-12 text-center"><p className="text-muted-foreground" data-testid="internal-no-results">{`Sin resultados para "${search}"`}</p></CardContent></Card>;
                }
                return filtered.map((cert) => (
                  <Card key={cert.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center shrink-0">
                            <Award className="w-6 h-6 text-success" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{cert.user_name}</h3>
                            <p className="text-sm text-muted-foreground">{cert.course_title}</p>
                            <p className="text-xs text-muted-foreground font-mono mt-1">{cert.code}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <Badge variant={cert.status === 'valid' ? 'default' : 'destructive'}>
                            {cert.status === 'valid' ? 'Válido' : 'Revocado'}
                          </Badge>
                          <Button size="sm" variant="outline" onClick={() => openEdit(cert)} data-testid={`edit-cert-${cert.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openQrOptions(cert.id)} data-testid={`qr-options-${cert.id}`}>
                            <Settings className="w-4 h-4 mr-1" />QR
                          </Button>
                          <a href={downloadCertificate(cert.id)} download target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" data-testid={`download-cert-${cert.id}`}>
                              <Download className="w-4 h-4 mr-1" />PDF
                            </Button>
                          </a>
                        </div>
                      </div>
                      {cert.revoked_reason && (
                        <p className="text-xs text-destructive mt-2 ml-16">Razón: {cert.revoked_reason}</p>
                      )}
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>
          </>
        )}

        {/* ── External certificates ── */}
        {activeTab === 'external' && (
          <div className="grid grid-cols-1 gap-4">
            {externalCerts.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <ExternalLink className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium mb-1">No hay certificados externos todavía</p>
                  <p className="text-sm text-muted-foreground mb-4">Sube un certificado externo para añadirle el QR de verificación.</p>
                  <Button onClick={() => navigate('/admin/certificates/upload-external')} data-testid="upload-external-empty-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Subir Primer Certificado Externo
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex justify-end mb-2">
                  <Button
                    size="sm"
                    onClick={() => navigate('/admin/certificates/upload-external')}
                    data-testid="upload-external-list-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Subir Nuevo
                  </Button>
                </div>
                {(() => {
                  const filtered = externalCerts.filter(filterExternal);
                  if (filtered.length === 0) {
                    return <Card><CardContent className="py-12 text-center"><p className="text-muted-foreground" data-testid="external-no-results">{`Sin resultados para "${search}"`}</p></CardContent></Card>;
                  }
                  return filtered.map((cert) => (
                  <Card key={cert.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <ExternalLink className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-0.5">
                              <h3 className="font-semibold">{cert.owner_name}</h3>
                              <Badge variant="secondary" className="text-xs">Externo</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{cert.course_name}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <p className="text-xs font-mono text-muted-foreground">{cert.code}</p>
                              {cert.issue_date && (
                                <p className="text-xs text-muted-foreground">· {cert.issue_date}</p>
                              )}
                              <p className="text-xs text-muted-foreground capitalize">· QR: {cert.qr_position === 'bottom-right' ? 'inf. derecha' : cert.qr_position === 'bottom-left' ? 'inf. izquierda' : 'personalizado'}</p>
                            </div>
                            {cert.user_id && (
                              <Badge className="mt-1 bg-blue-100 text-blue-800 border border-blue-200 text-xs gap-1" data-testid={`external-assigned-badge-${cert.id}`}>
                                <UserCheck className="w-3 h-3" />
                                Asignado {cert.owner_email ? `· ${cert.owner_email}` : ''}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <Badge variant={cert.status === 'valid' ? 'default' : 'destructive'}>
                            {cert.status === 'valid' ? 'Válido' : 'Revocado'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/certificates/upload-external/edit/${cert.id}`)}
                            data-testid={`edit-external-${cert.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <a href={downloadExternalCertificate(cert.id)} download target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" data-testid={`download-external-${cert.id}`}>
                              <Download className="w-4 h-4 mr-1" />PDF
                            </Button>
                          </a>
                          <Button
                            size="sm"
                            variant={cert.user_id ? "secondary" : "outline"}
                            onClick={() => cert.user_id
                              ? handleUnassign('external', cert.id)
                              : setAssignDialog({ open: true, type: 'external', certId: cert.id })
                            }
                            data-testid={`assign-external-${cert.id}`}
                            title={cert.user_id ? 'Quitar asignación' : 'Asignar a usuario registrado'}
                          >
                            {cert.user_id ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteExternal(cert.id, cert.owner_name)}
                            data-testid={`delete-external-${cert.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {cert.revoked_reason && (
                        <p className="text-xs text-destructive mt-2 ml-16">Razón: {cert.revoked_reason}</p>
                      )}
                    </CardContent>
                  </Card>
                ));
                })()}
              </>
            )}
          </div>
        )}

        {/* ── Legacy certificates ── */}
        {activeTab === 'legacy' && (
          <div className="space-y-6">
            {/* Stats + Actions bar */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Certificados Legacy Importados</h3>
                    {legacyStats ? (
                      <div className="flex flex-wrap gap-3">
                        <Badge variant="secondary" className="text-sm">Total: {legacyStats.total}</Badge>
                        {legacyStats.by_period.map(p => (
                          <Badge key={p.period} variant="outline" className="text-xs">{p.period}: {p.count}</Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Sin datos importados aún</p>
                    )}
                    {legacyImportStatus && legacyImportStatus.status !== 'not_started' && (
                      <div className="mt-2">
                        <Badge variant={legacyImportStatus.status === 'running' ? 'secondary' : 'default'} className="text-xs">
                          Estado: {legacyImportStatus.status === 'running' ? 'Importando...' : `Completado — ${legacyImportStatus.total} certificados`}
                        </Badge>
                        {legacyImportStatus.finished_at && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {new Date(legacyImportStatus.finished_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleImport}
                      disabled={importing}
                      data-testid="import-legacy-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${importing ? 'animate-spin' : ''}`} />
                      {importing ? 'Importando...' : legacyStats?.total > 0 ? 'Re-importar' : 'Importar Ahora'}
                    </Button>
                    {legacyStats?.total > 0 && (
                      <Button variant="destructive" size="sm" onClick={handleDeleteAllLegacy} data-testid="delete-all-legacy-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Search */}
            <form
              onSubmit={(e) => { e.preventDefault(); handleLegacySearch(legacyQuery, 1); }}
              className="flex gap-3"
            >
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por folio o nombre..."
                  value={legacyQuery}
                  onChange={(e) => setLegacyQuery(e.target.value)}
                  className="pl-10"
                  data-testid="legacy-admin-search"
                />
              </div>
              <Button type="submit" variant="outline" disabled={legacySearching} data-testid="legacy-admin-search-btn">
                Buscar
              </Button>
            </form>

            {/* Results */}
            {legacyResults.length > 0 ? (
              <>
                <div className="text-sm text-muted-foreground">{legacyTotal} resultado{legacyTotal !== 1 ? 's' : ''}</div>
                <div className="grid grid-cols-1 gap-3">
                  {legacyResults.map((cert) => (
                    <Card key={cert.id} className="hover:shadow-sm transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                              <Archive className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-semibold">{cert.student_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">Folio: {cert.folio} · {cert.period}</p>
                              {cert.user_id && (
                                <Badge className="mt-1 bg-blue-100 text-blue-800 border border-blue-200 text-xs gap-1" data-testid={`legacy-assigned-badge-${cert.id}`}>
                                  <UserCheck className="w-3 h-3" />
                                  Asignado {cert.student_email ? `· ${cert.student_email}` : ''}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant={cert.user_id ? "secondary" : "outline"}
                              onClick={() => cert.user_id
                                ? handleUnassign('legacy', cert.id)
                                : setAssignDialog({ open: true, type: 'legacy', certId: cert.id })
                              }
                              data-testid={`assign-legacy-${cert.id}`}
                              title={cert.user_id ? 'Quitar asignación' : 'Asignar a usuario registrado'}
                            >
                              {cert.user_id ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                            </Button>
                            <a href={cert.image_url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" variant="outline" data-testid={`view-legacy-${cert.folio}`}>
                                <ExternalLink className="w-4 h-4 mr-1" /> Ver
                              </Button>
                            </a>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {/* Pagination */}
                {Math.ceil(legacyTotal / LEGACY_LIMIT) > 1 && (
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" size="sm" disabled={legacyPage <= 1} onClick={() => handleLegacySearch(legacyQuery, legacyPage - 1)}>Anterior</Button>
                    <span className="px-3 py-1.5 text-sm text-muted-foreground">{legacyPage} / {Math.ceil(legacyTotal / LEGACY_LIMIT)}</span>
                    <Button variant="outline" size="sm" disabled={legacyPage >= Math.ceil(legacyTotal / LEGACY_LIMIT)} onClick={() => handleLegacySearch(legacyQuery, legacyPage + 1)}>Siguiente</Button>
                  </div>
                )}
              </>
            ) : (
              !legacySearching && (
                <div className="text-center py-12 text-muted-foreground">
                  <Archive className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No hay certificados legacy importados</p>
                  <p className="text-sm mt-1">Presiona "Importar Ahora" para cargar los certificados desde el servidor externo.</p>
                </div>
              )
            )}
          </div>
        )}

      </div>
      <AssignUserDialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false, type: null, certId: null })}
        onSelect={handleAssignUser}
      />
    </div>
  );
};

export default ManageCertificates;
